import { Typography } from '@yiluhub/ui-elements';
import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ExternalLinkModal from 'modules/landing-page/components/ExternalLinkModal';
import { NoOffersFound } from 'modules/landing-page/components/NoOffersFound';
import StopoverCard from 'modules/landing-page/components/StopoverCard';
import { useExternalLinkModal } from 'modules/landing-page/hooks/useExternalLinkModal';
import { usePageLoad } from 'modules/landing-page/hooks/usePageLoad';
import { sendCardBknEvent, sendCardInfEvent } from 'modules/landing-page/utils/tracking';
import { EventParam } from 'modules/landing-page/utils/types';

import './styles.scss';
import { useSessionKey } from './useSessionKey';

const TOUCHPOINT_TYPE = 'stopover_page';
const PRODUCT_CARD_ORDER = 'BHF-ROW-LNH-IGV-BNG-NST-MCT-GRM-MCC-ZUG-SXC-SXR-FMG';

const ASSETS_BASE_URL =
  'https://provider-resources-production-v2.s3.eu-central-1.amazonaws.com/landingpage/assets/';

const getProductImage = (productNumber: string, extension: string = 'jpg'): string => {
  return `${ASSETS_BASE_URL}stopover/images/product_${productNumber}.${extension}`;
};

const getBannerImage = (screen?: string): string => {
  if (screen === 'mobile') {
    return `${ASSETS_BASE_URL}stopover/images/stopover-header_mobile.jpg`;
  }
  return `${ASSETS_BASE_URL}stopover/images/stopover-header.jpg`;
};

export const LandingPageBody: React.FC = () => {
  const hasSessionKey = useSessionKey();
  // fire load event once page loads
  const { handleClickCallback } = usePageLoad({
    touchpointType: TOUCHPOINT_TYPE,
    productCardOrder: PRODUCT_CARD_ORDER,
  });

  const {
    isModalVisible,
    skipModal,
    handleCardButtonClick,
    handleConfirm,
    handleCancel,
    handleSkipModalChange,
  } = useExternalLinkModal();

  const { t } = useTranslation();

  return (
    <>
      {hasSessionKey && (
        <div className="stopover-lp_mainContainer">
          <Typography variant="h1" className="stopover-lp-heading">
            {t('stopover.page.heading.text')}
          </Typography>
          <section className="stopover-lp-container">
            <div className="stopover-lp-header-image">
              <picture>
                <source media="(max-width: 500px" srcSet={getBannerImage('mobile')} />
                <source media="(max-width: 900px" srcSet={getBannerImage('mobile')} />
                <img src={getBannerImage()} alt="header" />
              </picture>
            </div>
            <div className="stopover-lp-header-text-container">
              <Typography variant="p1" className="stopover-lp-header-text">
                {t('stopover.page.subHeading.text')}
              </Typography>
            </div>
          </section>
          <section className="stopover-lp-cards-section">
            <div className="stopover-lp-cards-container">
              <StopoverCard
                dataTestId="stopover-product13"
                heading={t('stopover.card.product13.heading')}
                subHeading={t('stopover.card.product13.subHeading')}
                image={getProductImage('13')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product13.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product13_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_BHF_BKN);
                  sendCardBknEvent('product13');
                  handleCardButtonClick(t('stopover.card.product13.button.url'));
                }}
                handleInfoClick={() => {
                  handleClickCallback(EventParam.CLICK_BHF_INF);
                  sendCardInfEvent('product13');
                }}
              />
              <StopoverCard
                dataTestId="stopover-product12"
                heading={t('stopover.card.product12.heading')}
                subHeading={t('stopover.card.product12.subHeading')}
                image={getProductImage('12')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product12.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product12_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_ROW_BKN);
                  sendCardBknEvent('product12');
                  handleCardButtonClick(t('stopover.card.product12.button.url'));
                }}
                handleInfoClick={() => {
                  handleClickCallback(EventParam.CLICK_ROW_INF);
                  sendCardInfEvent('product12');
                }}
              />
              <StopoverCard
                className="stopover-leonardo-card"
                dataTestId="stopover-product01"
                heading={t('stopover.card.product01.heading')}
                subHeading={t('stopover.card.product01.subHeading')}
                image={getProductImage('01')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product01.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product01_logo.png`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_LNH_BKN);
                  sendCardBknEvent('product01');
                  handleCardButtonClick(t('stopover.card.product01.button.url'));
                }}
                handleInfoClick={() => {
                  handleClickCallback(EventParam.CLICK_LNH_INF);
                  sendCardInfEvent('product01');
                }}
              />
              <StopoverCard
                dataTestId="stopover-product10"
                heading={t('stopover.card.product10.heading')}
                subHeading={t('stopover.card.product10.subHeading')}
                image={getProductImage('10')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product10.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product10_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_IGV_BKN);
                  sendCardBknEvent('product10');
                  handleCardButtonClick(t('stopover.card.product10.button.url'));
                }}
                handleInfoClick={() => {
                  handleClickCallback(EventParam.CLICK_IGV_INF);
                  sendCardInfEvent('product10');
                }}
              />
              <StopoverCard
                dataTestId="stopover-product09"
                heading={t('stopover.card.product09.heading')}
                subHeading={t('stopover.card.product09.subHeading')}
                image={getProductImage('09')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.product09.cta.button.text')}
                linkUrl={t('stopover.card.product09.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product09_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_BNG_DWN);
                  sendCardBknEvent('product09');
                  handleCardButtonClick(t('stopover.card.product09.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product09');
                  handleClickCallback(EventParam.CLICK_BNG_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product04"
                heading={t('stopover.card.product04.heading')}
                subHeading={t('stopover.card.product04.subHeading')}
                image={getProductImage('04')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product04.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product04_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_NST_BKN);
                  sendCardBknEvent('product04');
                  handleCardButtonClick(t('stopover.card.product04.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product04');
                  handleClickCallback(EventParam.CLICK_NST_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product05"
                heading={t('stopover.card.product05.heading')}
                subHeading={t('stopover.card.product05.subHeading')}
                image={getProductImage('05')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product05.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product05_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_MCT_BKN);
                  sendCardBknEvent('product05');
                  handleCardButtonClick(t('stopover.card.product05.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product05');
                  handleClickCallback(EventParam.CLICK_MCT_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product03"
                heading={t('stopover.card.product03.heading')}
                subHeading={t('stopover.card.product03.subHeading')}
                image={getProductImage('03')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product03.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product03_logo.png`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_GRM_BKN);
                  sendCardBknEvent('product03');
                  handleCardButtonClick(t('stopover.card.product03.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product03');
                  handleClickCallback(EventParam.CLICK_GRM_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product06"
                heading={t('stopover.card.product06.heading')}
                subHeading={t('stopover.card.product06.subHeading')}
                image={getProductImage('06')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product06.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product06_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_MCC_BKN);
                  sendCardBknEvent('product06');
                  handleCardButtonClick(t('stopover.card.product06.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product06');
                  handleClickCallback(EventParam.CLICK_MCC_INF);
                }}
              />
              <StopoverCard
                className="stopover-zugspitze-card"
                dataTestId="stopover-product02"
                heading={t('stopover.card.product02.heading')}
                subHeading={t('stopover.card.product02.subHeading')}
                image={getProductImage('02')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product02.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product02_logo_cropped.png`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_ZUG_BKN);
                  sendCardBknEvent('product02');
                  handleCardButtonClick(t('stopover.card.product02.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product02');
                  handleClickCallback(EventParam.CLICK_ZUG_INF);
                }}
              />
              <StopoverCard
                className="stopover-sixt-card"
                dataTestId="stopover-product08"
                heading={t('stopover.card.product08.heading')}
                subHeading={t('stopover.card.product08.subHeading')}
                image={getProductImage('08')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product08.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product08_logo.png`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_SXC_BKN);
                  sendCardBknEvent('product08');
                  handleCardButtonClick(t('stopover.card.product08.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product08');
                  handleClickCallback(EventParam.CLICK_SXC_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product11"
                heading={t('stopover.card.product11.heading')}
                subHeading={t('stopover.card.product11.subHeading')}
                image={getProductImage('11')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product11.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product11_logo.png`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_SXR_BKN);
                  sendCardBknEvent('product11');
                  handleCardButtonClick(t('stopover.card.product11.button.url'));
                }}
                handleInfoClick={() => {
                  sendCardInfEvent('product11');
                  handleClickCallback(EventParam.CLICK_SXR_INF);
                }}
              />
              <StopoverCard
                dataTestId="stopover-product14"
                heading={t('stopover.card.product14.heading')}
                subHeading={t('stopover.card.product14.subHeading')}
                image={getProductImage('14')}
                ctaLabel={t('stopover.card.cta.link.text')}
                buttonLabel={t('stopover.card.cta.button.text')}
                linkUrl={t('stopover.card.product14.moreinfo.url.text')}
                cornerLogo={`${ASSETS_BASE_URL}stopover/logos/product14_logo.jpg`}
                handleClick={() => {
                  handleClickCallback(EventParam.CLICK_FMG_BKN);
                  sendCardBknEvent('product14');
                  handleCardButtonClick(t('stopover.card.product14.button.url'));
                }}
                handleInfoClick={() => {
                  handleClickCallback(EventParam.CLICK_FMG_INF);
                  sendCardInfEvent('product14');
                }}
              />
            </div>
            <div>
              <ElementsSDK.Message
                icon={ElementsSDK.Icon.Info}
                content={t('stopover.page.bottom.info.text')}
              ></ElementsSDK.Message>
            </div>
          </section>
        </div>
      )}
      {!hasSessionKey && (
        <div className="stopover-no-offers-found-container">
          <NoOffersFound />
        </div>
      )}
      <ExternalLinkModal
        isVisible={isModalVisible}
        skipModal={skipModal}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onSkipModalChange={handleSkipModalChange}
      />
    </>
  );
};

const Stopover: React.FC = () => {
  return (
    <div className="lp-layout">
      <LandingPageBody />
    </div>
  );
};

export default Stopover;
